import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface AuthContextType {
  isSessionExpired: boolean;
  setSessionExpired: (expired: boolean) => void;
  triggerSessionExpired: () => void;
  refreshTokenCallback: (() => Promise<string | null>) | null;
  setRefreshTokenCallback: (callback: () => Promise<string | null>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [refreshTokenCallback, setRefreshTokenCallbackState] = useState<(() => Promise<string | null>) | null>(null);

  const setSessionExpired = useCallback((expired: boolean) => {
    setIsSessionExpired(expired);
  }, []);

  const triggerSessionExpired = useCallback(() => {
    setIsSessionExpired(true);
  }, []);

  const setRefreshTokenCallback = useCallback((callback: () => Promise<string | null>) => {
    setRefreshTokenCallbackState(() => callback);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isSessionExpired,
        setSessionExpired,
        triggerSessionExpired,
        refreshTokenCallback,
        setRefreshTokenCallback,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
